import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { auth, db } from '../config/firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import type { Convention, ConventionEmployee, ConventionInput, ConventionEmployeeInput } from '../types/convention';
import { withCrmType } from '../utils/crmType';

interface ConventionsState {
  conventions: Convention[];
  employees: ConventionEmployee[];
  loading: boolean;
  error: string | null;
  loadConventions: () => Promise<void>;
  loadEmployees: (conventionId: string) => Promise<void>;
  loadAllEmployees: () => Promise<void>;
  createConvention: (data: ConventionInput) => Promise<Convention | null>;
  updateConvention: (id: string, data: Partial<ConventionInput>) => Promise<boolean>;
  deleteConvention: (id: string) => Promise<void>;
  addEmployee: (conventionId: string, data: ConventionEmployeeInput) => Promise<void>;
  updateEmployee: (id: string, data: Partial<ConventionEmployeeInput>) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  bulkAddEmployees: (conventionId: string, employees: ConventionEmployeeInput[]) => Promise<{ created: number; updated: number }>;
  clearAllEmployees: (conventionId: string) => Promise<void>;
  syncAllToBeneficiaires: (conventionId: string) => Promise<number>;
}

export const useConventionsStore = create<ConventionsState>((set, get) => ({
  conventions: [],
  employees: [],
  loading: false,
  error: null,

  loadConventions: async () => {
    set({ loading: true, error: null });
    const { data, error } = await supabase
      .from('conventions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      set({ error: 'Une erreur est survenue lors du chargement des conventions', loading: false });
      return;
    }
    set({ conventions: (data as Convention[]) || [], loading: false });
  },

  loadEmployees: async (conventionId: string) => {
    const { data, error } = await supabase
      .from('convention_employees')
      .select('*')
      .eq('convention_id', conventionId)
      .order('created_at', { ascending: false });
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return;
    }
    set({ employees: (data as ConventionEmployee[]) || [] });
  },

  loadAllEmployees: async () => {
    const { data, error } = await supabase
      .from('convention_employees')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return;
    }
    set({ employees: (data as ConventionEmployee[]) || [] });
  },

  createConvention: async (data: ConventionInput) => {
    const { data: result, error } = await supabase
      .from('conventions')
      .insert(data)
      .select()
      .single();
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return null;
    }
    await get().loadConventions();
    return result as Convention;
  },


  updateConvention: async (id: string, data: Partial<ConventionInput>) => {
    const { data: result, error } = await supabase
      .from('conventions')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return false;
    }
    await get().loadConventions();
    set((state) => ({
      conventions: state.conventions.map(c =>
        c.id === id ? { ...c, ...result } as Convention : c
      ),
    }));
    return true;
  },

  deleteConvention: async (id: string) => {
    const { error } = await supabase
      .from('conventions')
      .delete()
      .eq('id', id);
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return;
    }
    await get().loadConventions();
  },

  addEmployee: async (conventionId: string, data: ConventionEmployeeInput) => {
    const { error } = await supabase
      .from('convention_employees')
      .insert({ ...data, convention_id: conventionId });
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return;
    }
    await get().loadEmployees(conventionId);
    await syncEmployeeToBeneficiaire(conventionId, data);
  },

  updateEmployee: async (id: string, data: Partial<ConventionEmployeeInput>) => {
    const { error } = await supabase
      .from('convention_employees')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      console.error('updateEmployee error:', error);
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      throw new Error(error.message);
    }
    const emp = get().employees.find(e => e.id === id);
    if (emp) {
      await get().loadEmployees(emp.convention_id);
    }
  },

  deleteEmployee: async (id: string) => {
    const emp = get().employees.find(e => e.id === id);
    const { error } = await supabase
      .from('convention_employees')
      .delete()
      .eq('id', id);
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      return;
    }
    if (emp) {
      await get().loadEmployees(emp.convention_id);
    }
  },

  bulkAddEmployees: async (conventionId: string, employees: ConventionEmployeeInput[]) => {
    const { data: existing, error: fetchError } = await supabase
      .from('convention_employees')
      .select('*')
      .eq('convention_id', conventionId);
    if (fetchError) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      throw new Error('Une erreur est survenue. Veuillez réessayer.');
    }

    const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const existingList = (existing as ConventionEmployee[]) || [];

    let created = 0;
    let updated = 0;
    const toInsert: ConventionEmployeeInput[] = [];

    for (const emp of employees) {
      const match = existingList.find(e =>
        norm(e.nom) === norm(emp.nom) && norm(e.prenom) === norm(emp.prenom)
      );
      if (match) {
        const merged: Partial<ConventionEmployeeInput> = {};
        (Object.keys(emp) as (keyof ConventionEmployeeInput)[]).forEach(key => {
          const newVal = emp[key];
          if (newVal && !match[key as keyof ConventionEmployee]) {
            (merged as any)[key] = newVal;
          }
        });
        if (Object.keys(merged).length > 0) {
          await supabase
            .from('convention_employees')
            .update({ ...merged, updated_at: new Date().toISOString() })
            .eq('id', match.id);
          updated++;
        }
      } else {
        toInsert.push(emp);
        created++;
      }
    }

    if (toInsert.length > 0) {
      const rows = toInsert.map(e => ({ ...e, convention_id: conventionId }));
      const { error: insertError } = await supabase
        .from('convention_employees')
        .insert(rows);
      if (insertError) {
        set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
        throw new Error('Une erreur est survenue. Veuillez réessayer.');
      }
    }

    await get().loadEmployees(conventionId);
    for (const emp of employees) {
      try {
        await syncEmployeeToBeneficiaire(conventionId, emp);
      } catch (syncError) {
        console.warn('Employee imported but candidate sync failed:', syncError);
      }
    }

    return { created, updated };
  },

  clearAllEmployees: async (conventionId: string) => {
    const { error } = await supabase
      .from('convention_employees')
      .delete()
      .eq('convention_id', conventionId);
    if (error) {
      set({ error: 'Une erreur est survenue. Veuillez réessayer.' });
      throw new Error('Une erreur est survenue. Veuillez réessayer.');
    }
    await get().loadEmployees(conventionId);
  },

  syncAllToBeneficiaires: async (conventionId: string) => {
    let count = 0;
    for (const emp of get().employees) {
      await syncEmployeeToBeneficiaire(conventionId, {
        nom: emp.nom, prenom: emp.prenom, email: emp.email, telephone: emp.telephone,
        telephone_portable: emp.telephone_portable, poste: emp.poste, statut: emp.statut,
        date_entree: emp.date_entree, date_fin: emp.date_fin, consultant_attribue: emp.consultant_attribue,
        notes: emp.notes, date_naissance: emp.date_naissance, age: emp.age, ville_dept: emp.ville_dept,
        poste_actuel: emp.poste_actuel
      });
      count++;
    }
    return count;
  },
}));

async function syncEmployeeToBeneficiaire(conventionId: string, emp: ConventionEmployeeInput) {
  if (!auth.currentUser) {
    console.warn('syncEmployeeToBeneficiaire: no authenticated user');
    return;
  }
  const convention = useConventionsStore.getState().conventions.find(c => c.id === conventionId);
  if (!convention) {
    console.warn('syncEmployeeToBeneficiaire: convention not found', conventionId);
    return;
  }

  // Avoid duplicates: check by email OR by name within the same convention
  const q = query(
    collection(db, 'beneficiaires'),
    where('conventionId', '==', conventionId)
  );
  const existing = await getDocs(q);
  const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const duplicate = existing.docs.some(d => {
    const data = d.data();
    if (emp.email && data.email && data.email === emp.email) return true;
    return norm(data.lastName) === norm(emp.nom) && norm(data.firstName) === norm(emp.prenom);
  });
  if (duplicate) return;

  try {
    await addDoc(collection(db, 'beneficiaires'), withCrmType({
      firstName: emp.prenom || '',
      lastName: emp.nom || '',
      title: emp.poste || '',
      phone: emp.telephone || emp.telephone_portable || '',
      email: emp.email || '',
      location: emp.ville_dept || '',
      availability: '',
      experiences: [],
      education: [],
      skills: [],
      languages: [],
      desiredPosition: '',
      currentPosition: emp.poste_actuel || emp.poste || '',
      yearsOfExperience: 0,
      formation: '',
      cvOk: false,
      age: emp.age ?? null,
      dateNaissance: emp.date_naissance ?? null,
      initialCompany: convention.entreprise_nom || '',
      conventionId: conventionId,
      conventionName: convention.nom || '',
      userId: auth.currentUser.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    }));
  } catch (err) {
    console.error('syncEmployeeToBeneficiaire: Firestore write failed', err);
    throw err;
  }
}